import React, { useEffect, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { SideBarTabSet, Spinner, Dialog, useControlledForm, ControlledForm, FormFieldProps, TabSetImperativeHandleRef, GeneralTabIcon, KeyTabIcon, NotesTabIcon, CustomFieldsIcon, IOption, ContactsTabIcon, UnitsTabIcon, useToast } from '@netfront/ui-library';
import { FieldErrors } from 'react-hook-form';
import { ICustomField, IUpdatedCustomField } from 'types';
import * as yup from 'yup';

import { getGroupDefaultValues, getCustomFieldResponses } from './ProjectGroupsSidebar.helpers';
import { ProjectGroupsSidebarProps } from './ProjectGroupsSidebar.interfaces';

import { useGetEntityCustomFields, useUpsertGroup, useGetGroupTypes, useGetGroupContacts } from '../../../hooks';
import { EntityCustomFieldsTab, UserGroupGeneralTab, UserGroupSmartCodesTab, UserGroupUnitsTab, UserGroupContactsTab, UserGroupNotesTab, IGroupUser } from '../../Tabs';

const ProjectGroupsSidebar = ({
  handleOpenCloseSideBar,
  hasUnits = false,
  isSideBarOpen = false,
  selectedGroup,
  projectId,
  onSave,
}: ProjectGroupsSidebarProps) => {

  const { handleToastError } = useToast();

  const [customFields, setCustomFields] = useState<IUpdatedCustomField[]>([]);
  const [defaultValues, setDefaultValues] = useState<FormFieldProps>();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [groupTypeOptions, setGroupTypeOptions] = useState<IOption[]>([]);
  const [groupContacts, setGroupContacts] = useState<IGroupUser[]>([]);

  const tabsetRef = useRef<TabSetImperativeHandleRef>(null);

  const { control, handleSubmit, reset, setValue } = useControlledForm({
    defaultValues,
    resolver: yupResolver(
      yup.object().shape({
        name: yup.string().label('Name').required(),
      }),
    ),
  });

  const handleResetAndReturn = () => {
    reset();
    onSave();
  };

  const handleCloseSidebar = () => {
    reset();
    handleOpenCloseSideBar();
  };

  const { handleGetGroupTypes, isLoading: isGetGroupTypesLoading = false } = useGetGroupTypes({
    onCompleted: ({ groupTypes: returnedGroupTypes }) => {
      setGroupTypeOptions(
        returnedGroupTypes.map(({ id, name }) => ({
          id,
          name,
          value: id,
        })),
      );
    },
    onError: (error) => {
      handleToastError({
        error,
      });
    },
  });

  const { handleGetGroupContacts, isLoading: isGetGroupContactsLoading = false } = useGetGroupContacts({
    onCompleted: ({ groupContacts: returnedGroupContacts = [] }) => {
      setGroupContacts(returnedGroupContacts);
    },
    onError: (error) => {
      handleToastError({
        error,
      });
    },
  });

  const { handleUpsertGroup, handleDeleteGroup, isLoading: isUpsertGroupLoading = false } = useUpsertGroup({
    onUpdate: () => handleResetAndReturn(),
  });

  const { handleGetEntityCustomFields, isLoading: isGetEntityCustomFieldsLoading = false } = useGetEntityCustomFields({
    fetchPolicy: 'no-cache',
    onCompleted: ({ customFields: returnedCustomFields }) => {
      const updatedCustomFields: IUpdatedCustomField[] = returnedCustomFields.map((customField) => {
        const { id: customFieldId, __typename, name: customFieldName, responses = [], availableOptions = [] } = customField as unknown as ICustomField;
        const defaultValue = __typename === 'CustomFieldBooleanGraphType' ? 'false' : '' ;
        const value = responses && responses.length ? responses[0].text ?? responses[0].value ?? responses[0].dateTime ?? responses[0].number : defaultValue;

        return {
          customFieldId,
          customFieldName,
          text: String(value ?? ''),
          __typename,
          availableOptions,
        };
      });
      setCustomFields(updatedCustomFields);
    },
    onError: (error) => {
      handleToastError({
        error,
      });
    },
  });

  const handleSave = (item: FormFieldProps) => {  
    const updatedCustomFields = getCustomFieldResponses({ item, customFields });

    handleUpsertGroup({
      projectId: String(projectId),
      groupId: selectedGroup?.id,
      item,
      responses: updatedCustomFields,
    });
  };

  const triggerTabsOnErrorHandler = (errs: FormFieldProps) => {
    if (tabsetRef.current) {
      tabsetRef.current.handleError(errs);
    }
  };

  const triggerTabsOnSuccessHandler = () => {
    if (tabsetRef.current) {
      tabsetRef.current.handleSuccess();
    }
  };

  useEffect(() => {
    if (!(projectId && isSideBarOpen)) return;

    void handleGetGroupTypes({
      projectId,
    });

    void handleGetEntityCustomFields({
      projectId,
      scope: 'GROUP',
      groupId: selectedGroup?.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, isSideBarOpen, projectId]);


  useEffect(() => {
    if (!(isSideBarOpen && selectedGroup)) {
      return;
    }

    void handleGetGroupContacts({
      groupId: selectedGroup.id,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSideBarOpen, selectedGroup]);


  useEffect(() => {
    setDefaultValues(getGroupDefaultValues({
      group: selectedGroup,
      customFields,
    }));
  }, [selectedGroup, customFields]);

  return (
    <>
      <Spinner isLoading={isGetGroupTypesLoading || isGetEntityCustomFieldsLoading || isUpsertGroupLoading || isGetGroupContactsLoading} />
      <ControlledForm
        callBack={(item: FormFieldProps) => {
          triggerTabsOnSuccessHandler();
          handleSave(item);
        }}
        handleSubmit={handleSubmit}
        onSubmitError={(errs: FieldErrors<FormFieldProps>) => {
          triggerTabsOnErrorHandler(errs as FormFieldProps);
        }}
      >
        <SideBarTabSet
          defaultActiveTabId="id_general_tab"
          handleOpenCloseSideBar={() => {
            handleCloseSidebar();
          }}
          hasViewPadding={false}
          isSideBarOpen={isSideBarOpen}
          tabs={[
            {
              icon: GeneralTabIcon,
              id: 'id_general_tab',
              label: 'General',
              view: () => (
                <UserGroupGeneralTab
                  control={control}
                  groupId={selectedGroup?.id}
                  groupTypeOptions={groupTypeOptions}
                  initialGroupName={defaultValues?.name}
                />
              ),
            },
            {
              icon: CustomFieldsIcon,
              id: 'id_custom_fields_tab',
              label: 'Extra info',
              view: () => (
                <EntityCustomFieldsTab 
                  control={control}
                  customFields={customFields}
                />
              ),
            },
            {
              icon: ContactsTabIcon,
              id: 'id_contacts_tab',
              label: 'Contacts',
              isHidden: !selectedGroup,
              view: () => selectedGroup ? (
                <UserGroupContactsTab 
                  allGroupContacts={groupContacts}
                  selectedGroupId={selectedGroup.id}
                  onUpdateContact={() => {
                    void handleGetGroupContacts({
                      groupId: selectedGroup.id
                    });
                  }}
                  />
              ) : <></>,
            },
            {
              icon: NotesTabIcon,
              id: 'id_notes_tab',
              label: 'Notes',
              isHidden: !selectedGroup,
              view: () => selectedGroup ? <UserGroupNotesTab selectedGroupId={selectedGroup.id} /> : <></>,
            },
            {
              icon: UnitsTabIcon,
              id: 'id_units_tab',
              label: 'Units',
              isHidden: !hasUnits || !selectedGroup,
              view: () => selectedGroup ? <UserGroupUnitsTab selectedGroup={selectedGroup} /> : <></>,
            },
            {
              icon: KeyTabIcon,
              id: 'id_codes_tab',
              label: 'Codes',
              isHidden: !selectedGroup,
              view: () => selectedGroup ? (
                <UserGroupSmartCodesTab
                  projectId={String(projectId)}
                  selectedGroup={selectedGroup}
                  setValue={setValue}
                />
              ): <></>,
            },
            
          ]}
          onDelete={selectedGroup ? () => setIsDeleteDialogOpen(true) : undefined}
          onSaveButtonType="submit"
        />
      </ControlledForm>
      {selectedGroup && (
        <Dialog
          isOpen={isDeleteDialogOpen}
          title={`Delete ${String(defaultValues?.name ?? 'group')}?`}
          isNarrow
          onCancel={() => setIsDeleteDialogOpen(false)}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={() => {
            if (!projectId) {
              return;
            }

            void handleDeleteGroup({
              groupId: selectedGroup.id,
            });
          }}
        />
      )}
    </>
  );
};

export { ProjectGroupsSidebar };
